import Icon from "@components/Atoms/Icon";
import t from "@ext/localization/locale/translate";
import PropertyButtons from "@ext/properties/components/Helpers/PropertyButtons";
import { getPropertyValueLabel } from "@ext/properties/logic/utils/getPropertyValueLabel";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuEmptyItem,
	DropdownMenuItem,
	DropdownMenuSearchItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	useSearchableMenu,
} from "@ui-kit/Dropdown";
import { MenuItem } from "@ui-kit/MenuItem";
import { ScrollShadowContainer } from "@ui-kit/ScrollShadowContainer";
import { useMemo } from "react";
import { ClearPropertyValueButton } from "./ClearPropertyValueButton";
import { PropertyValueTags } from "./PropertyValueTags";
import type { PropertyValueProps } from "./types";

interface ChoicePropertyValueProps extends PropertyValueProps {
	multiple: boolean;
}

const SEARCHABLE_VALUES_COUNT = 8;

export const ChoicePropertyValue = ({
	disabled,
	isAssigned,
	multiple,
	onDelete,
	onSubmit,
	property,
}: ChoicePropertyValueProps) => {
	const { search, setSearch, contentRef, inputRef, handleContentKeyDown, handleInputKeyDown, filterItems } =
		useSearchableMenu();

	const values = property.values;
	const isSearchable = (values?.length ?? 0) > SEARCHABLE_VALUES_COUNT;

	const filteredValues = useMemo(() => {
		if (!values) return [];
		if (!isSearchable) return values;

		const labeled = values.map((value) => ({ label: getPropertyValueLabel(property.name, value), value }));
		return filterItems(labeled).map((item) => item.value);
	}, [filterItems, isSearchable, property.name, values]);

	const clear = () => onDelete(property.id);

	return (
		<DropdownMenu modal={false} onOpenChange={(open) => !open && setSearch("")}>
			<DropdownMenuTrigger asChild disabled={disabled}>
				<MenuItem
					aria-label={property.name}
					className="group min-h-7 w-full min-w-0 items-center gap-1 rounded-lg bg-transparent px-1.5 py-0.5 hover:bg-secondary-bg-hover focus-visible:bg-secondary-bg-hover data-[state=open]:bg-secondary-bg-hover"
					onMouseDown={(event) => event.preventDefault()}
					role="button"
					tabIndex={disabled ? -1 : 0}
				>
					<div className="min-w-0 flex-1">
						<PropertyValueTags property={property} />
					</div>
					{isAssigned && !disabled && (
						<ClearPropertyValueButton className="flex h-6 items-center self-start" onClear={clear} />
					)}
				</MenuItem>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="start"
				className="min-w-48"
				onCloseAutoFocus={(event) => event.preventDefault()}
				onKeyDown={isSearchable ? handleContentKeyDown : undefined}
				ref={contentRef}
			>
				{isSearchable && (
					<>
						<DropdownMenuSearchItem
							onChange={(event) => setSearch(event.target.value)}
							onClick={(event) => event.stopPropagation()}
							onKeyDown={handleInputKeyDown}
							placeholder={t("search.placeholder")}
							ref={inputRef}
							value={search}
						/>
						<DropdownMenuSeparator />
					</>
				)}
				{filteredValues.length ? (
					<ScrollShadowContainer className="max-h-48">
						<PropertyButtons
							name={property.name}
							onChange={(value) => onSubmit(property.id, value)}
							options={multiple ? { closeOnSelect: true } : undefined}
							type={multiple ? "checkbox" : "radio"}
							value={property.value ?? []}
							values={filteredValues}
						/>
					</ScrollShadowContainer>
				) : (
					<DropdownMenuEmptyItem>
						{search ? t("properties.no-values") : t("properties.empty")}
					</DropdownMenuEmptyItem>
				)}
				{isAssigned && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={() => onDelete(property.id)}>
							<Icon code="eraser" />
							{t("clear")}
						</DropdownMenuItem>
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
